import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import ReviewCard from "@/components/base/ReviewCard";
import { movies } from "@/mocks/movies";
import { genreCategories, yearFilters } from "@/mocks/categories";
import {
  PAGE_SIZE,
  getPopularReviews,
  paginate,
  sortMovies,
  type SortKey,
} from "@/utils/catalog";

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "updated", label: "最近更新", icon: "ri-time-line" },
  { key: "rating", label: "评分最高", icon: "ri-star-line" },
  { key: "views", label: "热度最高", icon: "ri-fire-line" },
];

export default function Category() {
  usePageMeta(
    "苍井空电影分类 - 苍井空电影大全",
    "苍井空电影分类页汇总全部影视作品，支持按剧情、爱情、悬疑、文艺、纪录、动作等题材与上映年份筛选，并结合评分与热度排序快速浏览。",
  );
  const [searchParams] = useSearchParams();
  const initialGenre = searchParams.get("genre");
  const [selectedGenres, setSelectedGenres] = useState<string[]>(
    initialGenre ? [initialGenre] : [],
  );
  const [year, setYear] = useState("all");
  const [sort, setSort] = useState<SortKey>("updated");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    if (initialGenre) {
      setSelectedGenres([initialGenre]);
      setPage(1);
    }
  }, [initialGenre]);

  const filtered = useMemo(() => {
    const list = movies.filter((movie) => {
      const genreMatch =
        selectedGenres.length === 0 ||
        selectedGenres.some((genre) => movie.genres.includes(genre));
      let yearMatch = true;
      if (year !== "all") {
        const [start, end] = year.split("-");
        const startYear = Number(start);
        const endYear = end ? Number(end) : startYear;
        yearMatch = movie.year >= startYear && movie.year <= endYear;
      }
      return genreMatch && yearMatch;
    });
    return sortMovies(list, sort);
  }, [selectedGenres, year, sort]);

  const { items, totalPages, current } = paginate(filtered, page);

  const toggleGenre = (genre: string) => {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((item) => item !== genre) : [...prev, genre],
    );
    setPage(1);
  };

  const resetFilters = () => {
    setSelectedGenres([]);
    setYear("all");
    setSort("updated");
    setPage(1);
  };

  const activeFilterCount = selectedGenres.length + (year !== "all" ? 1 : 0);
  const popular = getPopularReviews(4);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1320px] px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb items={[{ label: "电影分类" }]} />

        <header className="mt-5 border-b border-background-200 pb-6">
          <h1 className="font-heading text-2xl text-foreground-950 md:text-[32px]">苍井空电影分类</h1>
          <p className="mt-2 max-w-3xl text-xs leading-relaxed text-foreground-600 md:text-[13px]">
            按题材与上映年份浏览全部 {movies.length} 部作品。可多选类型标签，再结合年份与排序方式，
            快速找到想看的影片；每部作品均有独立资料页与相关影评。
          </p>
        </header>

        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_300px]">
          <div className="min-w-0">
            <button
              onClick={() => setFilterOpen((prev) => !prev)}
              className="mb-3 flex w-full items-center justify-between rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-700 lg:hidden"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line text-base text-primary-600" />
                筛选条件
                {activeFilterCount > 0 ? (
                  <span className="rounded-full bg-primary-600 px-1.5 py-0.5 text-[10px] text-background-50">
                    {activeFilterCount}
                  </span>
                ) : null}
              </span>
              <i className={`${filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-lg`} />
            </button>

            <div
              className={`rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5 ${
                filterOpen ? "block" : "hidden lg:block"
              }`}
            >
              <div>
                <p className="flex items-center gap-2 text-xs text-foreground-500">
                  <span className="font-heading text-sm text-foreground-900">电影类型</span>
                  <span className="text-[11px] text-foreground-400">可多选</span>
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {genreCategories.map((category) => {
                    const active = selectedGenres.includes(category.name);
                    return (
                      <button
                        key={category.name}
                        onClick={() => toggleGenre(category.name)}
                        aria-pressed={active}
                        className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                          active
                            ? "border-primary-600 bg-primary-600 text-background-50"
                            : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                        }`}
                      >
                        <i className={`${category.icon} text-sm leading-none`} />
                        {category.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 grid gap-4 border-t border-background-200 pt-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs text-foreground-500">上映年份</span>
                  <select
                    value={year}
                    onChange={(event) => {
                      setYear(event.target.value);
                      setPage(1);
                    }}
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
                  >
                    {yearFilters.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs text-foreground-500">排序方式</span>
                  <select
                    value={sort}
                    onChange={(event) => {
                      setSort(event.target.value as SortKey);
                      setPage(1);
                    }}
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
                <p className="text-[11px] text-foreground-500">
                  已选：
                  {activeFilterCount === 0
                    ? "全部作品"
                    : [...selectedGenres, year === "all" ? "" : yearFilters.find((item) => item.value === year)?.label ?? ""]
                        .filter(Boolean)
                        .join(" · ")}
                </p>
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-1 whitespace-nowrap text-[11px] text-primary-700 transition-colors hover:text-primary-800"
                >
                  <i className="ri-refresh-line text-sm" />
                  重置筛选
                </button>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-foreground-500">
                共找到 <span className="font-medium text-foreground-900">{filtered.length}</span> 部作品
                <span className="mx-2 text-background-400">|</span>
                第 {current} / {totalPages} 页
              </p>
              <div className="hidden items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1 md:flex">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    onClick={() => {
                      setSort(option.key);
                      setPage(1);
                    }}
                    className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] transition-colors ${
                      sort === option.key
                        ? "bg-background-50 text-primary-700"
                        : "text-foreground-500 hover:text-foreground-800"
                    }`}
                  >
                    <i className={`${option.icon} text-sm`} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {items.length ? (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {items.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} variant="row" />
                ))}
              </div>
            ) : (
              <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-14 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-xl leading-none" />
                </span>
                <h2 className="mt-4 font-heading text-base text-foreground-900">没有符合条件的作品</h2>
                <p className="mt-1.5 text-xs text-foreground-500">
                  当前筛选条件下没有匹配内容，试试减少类型或更换年份区间。
                </p>
                <button
                  onClick={resetFilters}
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-700"
                >
                  <i className="ri-refresh-line text-sm" />
                  重置全部筛选
                </button>
              </div>
            )}

            <Pagination page={current} totalPages={totalPages} onChange={setPage} />

            <p className="mt-6 text-center text-[11px] text-foreground-400">
              每页展示 {PAGE_SIZE} 部作品 · 点击卡片查看完整资料与影评
            </p>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base text-foreground-950">热门影评</h2>
              <div className="mt-2">
                {popular.map((review) => (
                  <ReviewCard key={review.id} review={review} variant="mini" />
                ))}
              </div>
              <Link
                to="/reviews"
                className="mt-3 inline-flex items-center gap-1 text-xs text-primary-700 hover:text-primary-800"
              >
                查看全部影评
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
              <h2 className="font-heading text-base text-foreground-950">题材速览</h2>
              <ul className="mt-3 space-y-2.5">
                {genreCategories.map((category) => {
                  const count = movies.filter((movie) => movie.genres.includes(category.name)).length;
                  return (
                    <li key={category.name}>
                      <button
                        onClick={() => {
                          setSelectedGenres([category.name]);
                          setPage(1);
                        }}
                        className="flex w-full items-center justify-between text-xs text-foreground-600 transition-colors hover:text-primary-700"
                      >
                        <span className="flex items-center gap-2">
                          <i className={`${category.icon} text-sm text-primary-500`} />
                          {category.name}
                        </span>
                        <span className="text-foreground-400">{count} 部</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <p className="flex items-center gap-2 text-[11px] tracking-[0.18em] text-accent-800">
                <i className="ri-information-line text-sm" />
                CONTENT NOTE
              </p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                本站资料按公开信息整理，剧情简介尽量不涉及关键情节。影评内容为作者个人观点，
                欢迎通过反馈表单补充或纠正信息。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}